/**
 * mm-optimistic-service.js
 *
 * Universal primitives for optimistic UI, debounced mutations, and action locks.
 * Consumed by wishlist hearts, restock toggles, cart operations, and notification switches.
 */
(function () {
  "use strict";

  if (window.MrMapleOptimistic) return;

  const DEFAULT_MIN_PENDING_MS = 180;
  const DEFAULT_DEBOUNCE_MS = 400;
  const TOGGLE_JOURNAL_STORAGE_KEY = "mrmaple_optimistic_toggle_journal_v1";
  const DEFAULT_RETRY_DELAYS_MS = [800, 1800, 3600];
  const flushableRegistry = new Set();
  let lifecycleBound = false;
  let toggleJournalCache = null;

  function canUseLocalStorage() {
    try {
      return Boolean(window.localStorage);
    } catch (_) {
      return false;
    }
  }

  function loadToggleJournal() {
    if (toggleJournalCache && typeof toggleJournalCache === "object") {
      return toggleJournalCache;
    }

    toggleJournalCache = {};
    if (!canUseLocalStorage()) {
      return toggleJournalCache;
    }

    try {
      const raw = window.localStorage.getItem(TOGGLE_JOURNAL_STORAGE_KEY);
      if (!raw) {
        return toggleJournalCache;
      }

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return toggleJournalCache;
      }

      Object.keys(parsed).forEach((key) => {
        const entry = parsed[key];
        if (!entry || typeof entry !== "object") return;
        if (typeof entry.value !== "boolean") return;
        toggleJournalCache[key] = {
          value: entry.value,
          updatedAt: Number(entry.updatedAt) || Date.now(),
        };
      });
    } catch (_) {}

    return toggleJournalCache;
  }

  function persistToggleJournal(nextJournal) {
    toggleJournalCache =
      nextJournal && typeof nextJournal === "object" ? nextJournal : {};

    if (!canUseLocalStorage()) {
      return toggleJournalCache;
    }

    try {
      const keys = Object.keys(toggleJournalCache);
      if (!keys.length) {
        window.localStorage.removeItem(TOGGLE_JOURNAL_STORAGE_KEY);
      } else {
        window.localStorage.setItem(
          TOGGLE_JOURNAL_STORAGE_KEY,
          JSON.stringify(toggleJournalCache),
        );
      }
    } catch (_) {}

    return toggleJournalCache;
  }

  function getPersistedToggleIntent(key) {
    if (!key) return null;
    const entry = loadToggleJournal()[String(key)];
    if (!entry || typeof entry.value !== "boolean") {
      return null;
    }

    return {
      key: String(key),
      value: entry.value,
      updatedAt: Number(entry.updatedAt) || 0,
    };
  }

  function setPersistedToggleIntent(key, value) {
    if (!key) return null;

    const nextJournal = {
      ...loadToggleJournal(),
      [String(key)]: {
        value: Boolean(value),
        updatedAt: Date.now(),
      },
    };

    persistToggleJournal(nextJournal);
    return getPersistedToggleIntent(key);
  }

  function clearPersistedToggleIntent(key) {
    if (!key) return;

    const nextJournal = { ...loadToggleJournal() };
    delete nextJournal[String(key)];
    persistToggleJournal(nextJournal);
  }

  function listPersistedToggleIntents(prefix = "") {
    const normalizedPrefix = String(prefix || "");
    return Object.keys(loadToggleJournal())
      .filter((key) =>
        normalizedPrefix ? key.startsWith(normalizedPrefix) : true,
      )
      .map((key) => getPersistedToggleIntent(key))
      .filter(Boolean);
  }

  function ensureLifecycleBindings() {
    if (lifecycleBound) return;
    if (
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return;
    }

    lifecycleBound = true;

    const flushForLifecycle = () => {
      void flushAllPending({ reason: "pagehide", keepalive: true });
    };

    window.addEventListener("pagehide", flushForLifecycle, { passive: true });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushForLifecycle();
      }
    });
  }

  function registerFlushable(entry) {
    if (!entry || typeof entry.flush !== "function") {
      return () => {};
    }

    flushableRegistry.add(entry);
    ensureLifecycleBindings();

    return () => {
      flushableRegistry.delete(entry);
    };
  }

  async function flushAllPending(options = {}) {
    const tasks = Array.from(flushableRegistry).map((entry) => {
      try {
        return Promise.resolve(entry.flush(options));
      } catch (error) {
        return Promise.reject(error);
      }
    });

    return Promise.allSettled(tasks);
  }

  /**
   * createOptimisticToggle – for boolean toggles (heart, restock bell, notification switch).
   *
   * Provides:
   *  - Immediate UI flip via render()
   *  - syncPromise coalescing so rapid clicks collapse to one in-flight call
   *  - Optional debounce delay before the first network call
   *  - MIN_PENDING_MS to prevent flicker
   *  - Automatic revert + onError callback on failure
   *
   * @param {Object} opts
   * @param {function(boolean, {pending: boolean}): void} opts.render – update the UI
   * @param {function(boolean): Promise<*>} opts.commit – persist the state (true = on, false = off)
   * @param {function(Error): void} [opts.onError] – called after revert on failure
   * @param {number} [opts.minPendingMs] – minimum visible pending duration (default 180)
   * @param {number} [opts.debounceMs] – delay before first commit to absorb rapid toggles (default 0)
   * @param {boolean} [opts.initialCommitted] – initial committed state before hydration
   * @param {string} [opts.persistenceKey] – localStorage journal key for durable last-write-wins replay
   * @param {string} [opts.registryKey] – logical registry key for diagnostics
   * @param {number[]} [opts.retryDelaysMs] – bounded retry schedule for transient failures
   * @returns {{ toggle(): void, setDesired(boolean, Object=): void, setCommitted(boolean, Object=): void, flush(Object=): Promise<void>, destroy(): void, dispose(): void, getSnapshot(): Object }}
   */
  function createOptimisticToggle(opts) {
    const {
      render,
      commit,
      onError,
      minPendingMs = DEFAULT_MIN_PENDING_MS,
      debounceMs = 0,
      initialCommitted = false,
      persistenceKey = null,
      registryKey = null,
      retryDelaysMs = DEFAULT_RETRY_DELAYS_MS,
    } = opts;

    const persistedIntent = persistenceKey
      ? getPersistedToggleIntent(persistenceKey)
      : null;

    let desired = persistedIntent
      ? Boolean(persistedIntent.value)
      : Boolean(initialCommitted);
    let committed = Boolean(initialCommitted);
    let syncPromise = null;
    let destroyed = false;
    let debounceTimer = null;
    let debouncePromise = null;
    let debounceResolve = null;
    let retryTimer = null;
    let retryAttempt = 0;
    let pendingReason =
      persistedIntent && desired !== committed ? "replay" : null;

    const flushable = {
      key: registryKey || persistenceKey || null,
      flush: (options) => api.flush(options),
    };
    const unregisterFlushable = registerFlushable(flushable);

    function renderState(value, pending) {
      if (destroyed) return;
      render(value, { pending });
    }

    function clearRetry() {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    }

    function flushDebounceWaiter() {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      if (debounceResolve) {
        const resolve = debounceResolve;
        debounceResolve = null;
        debouncePromise = null;
        resolve();
      }
    }

    function ensureDebouncePromise() {
      if (debounceMs <= 0) {
        return Promise.resolve();
      }

      if (!debouncePromise) {
        debouncePromise = new Promise((resolve) => {
          debounceResolve = resolve;
        });
      }

      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        flushDebounceWaiter();
      }, debounceMs);

      return debouncePromise;
    }

    function persistDesiredState(value) {
      if (!persistenceKey) return;

      if (Boolean(value) === committed) {
        clearPersistedToggleIntent(persistenceKey);
        return;
      }

      setPersistedToggleIntent(persistenceKey, value);
    }

    function scheduleRetry(error) {
      if (
        destroyed ||
        !persistenceKey ||
        error?.unauthorized ||
        error?.status === 401
      ) {
        return;
      }

      const nextDelay =
        retryDelaysMs[Math.min(retryAttempt, retryDelaysMs.length - 1)] || 0;
      retryAttempt += 1;
      pendingReason = "retry";
      clearRetry();
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (destroyed || desired === committed) return;
        void syncDesired({ reason: "retry", immediate: true });
      }, nextDelay);
    }

    function isPending() {
      return Boolean(syncPromise || debounceTimer || retryTimer);
    }

    async function flushDesiredState(options = {}) {
      while (!destroyed && desired !== committed) {
        const target = desired;
        const pendingStart = Date.now();
        pendingReason = options.reason || pendingReason || "debounce";
        renderState(target, true);

        if (!options.immediate && debounceMs > 0) {
          await ensureDebouncePromise();
          if (destroyed) return;
          if (desired !== target) continue;
        }

        try {
          await commit(target, {
            reason: pendingReason,
            keepalive: options.keepalive === true,
            persistenceKey,
            registryKey,
          });
          committed = target;
          retryAttempt = 0;

          if (desired === committed) {
            clearPersistedToggleIntent(persistenceKey);
          }

          const elapsed = Date.now() - pendingStart;
          if (!options.skipMinPending && elapsed < minPendingMs) {
            await new Promise((resolve) =>
              setTimeout(resolve, minPendingMs - elapsed),
            );
          }

          if (desired !== target) {
            persistDesiredState(desired);
            continue;
          }

          pendingReason = null;
          renderState(target, false);
        } catch (error) {
          const shouldRevert =
            error?.unauthorized === true || error?.status === 401;

          if (desired === target) {
            if (shouldRevert) {
              desired = committed;
              clearRetry();
              clearPersistedToggleIntent(persistenceKey);
              pendingReason = null;
              renderState(committed, false);
            } else {
              persistDesiredState(desired);
              scheduleRetry(error);
              renderState(desired, false);
            }
          }

          if (typeof onError === "function") {
            onError(error, {
              reverted: shouldRevert,
              desired,
              committed,
              pending: isPending(),
              persistenceKey,
              registryKey,
            });
          }

          if (shouldRevert) {
            throw error;
          }
          return;
        }
      }
    }

    function syncDesired(options = {}) {
      if (destroyed) {
        return Promise.resolve();
      }

      if (desired === committed) {
        clearRetry();
        clearPersistedToggleIntent(persistenceKey);
        pendingReason = null;
        renderState(committed, false);
        return Promise.resolve();
      }

      persistDesiredState(desired);

      if (options.immediate) {
        flushDebounceWaiter();
      } else if (debounceMs > 0) {
        ensureDebouncePromise();
      }

      if (syncPromise) {
        pendingReason = options.reason || pendingReason || "debounce";
        return syncPromise;
      }

      syncPromise = flushDesiredState(options)
        .catch(() => {})
        .finally(() => {
          syncPromise = null;
          if (destroyed) return;
          if (desired !== committed && !retryTimer) {
            void syncDesired({ reason: "debounce" });
          }
        });

      return syncPromise;
    }

    const api = {
      toggle(options = {}) {
        api.setDesired(!desired, options);
      },

      setDesired(value, options = {}) {
        desired = Boolean(value);
        pendingReason = options.reason || pendingReason || "debounce";
        if (desired === committed) {
          clearRetry();
          clearPersistedToggleIntent(persistenceKey);
          pendingReason = null;
          renderState(committed, false);
          return;
        }
        persistDesiredState(desired);
        renderState(desired, true);
        void syncDesired({
          reason: options.reason || "debounce",
          immediate: options.immediate === true,
          keepalive: options.keepalive === true,
        });
      },

      setCommitted(value, options = {}) {
        committed = Boolean(value);
        const persisted = persistenceKey
          ? getPersistedToggleIntent(persistenceKey)
          : null;
        const shouldHoldDesired =
          options.forceDesired !== true &&
          persisted &&
          persisted.value !== committed;

        if (shouldHoldDesired) {
          desired = Boolean(persisted.value);
          pendingReason = "replay";
          renderState(desired, true);
          void syncDesired({ reason: "replay", immediate: true });
          return;
        }

        if (!isPending() || options.forceDesired === true) {
          desired = committed;
        }

        if (desired === committed) {
          clearRetry();
          clearPersistedToggleIntent(persistenceKey);
          pendingReason = null;
          renderState(committed, false);
          return;
        }

        persistDesiredState(desired);
        renderState(desired, true);
      },

      getDesired() {
        return desired;
      },
      getCommitted() {
        return committed;
      },
      isPending,
      getSnapshot() {
        return {
          desired,
          committed,
          pending: isPending(),
          pendingReason,
          persistedIntent: persistenceKey
            ? getPersistedToggleIntent(persistenceKey)
            : null,
          key: registryKey || persistenceKey || null,
        };
      },

      async flush(options = {}) {
        if (destroyed) return;
        if (desired === committed && !syncPromise) {
          clearPersistedToggleIntent(persistenceKey);
          return;
        }

        flushDebounceWaiter();

        if (syncPromise) {
          await syncPromise;
          return;
        }

        await syncDesired({
          reason: options.reason || "flush",
          immediate: true,
          keepalive: options.keepalive === true,
          skipMinPending: options.skipMinPending === true,
        });
      },

      destroy() {
        destroyed = true;
        flushDebounceWaiter();
        clearRetry();
        unregisterFlushable();
      },
      dispose() {
        api.destroy();
      },
    };

    if (persistedIntent && desired !== committed) {
      renderState(desired, true);
      void syncDesired({ reason: "replay", immediate: true });
    }

    return api;
  }

  /**
   * createDebouncedMutation – for value mutations (cart qty, text fields).
   *
   * Provides:
   *  - Immediate UI update via render()
   *  - Debounced network call
   *  - Deduplication of in-flight requests
   *  - Error revert via onError
   *
   * @param {Object} opts
   * @param {function(T): void} opts.render – update UI immediately
   * @param {function(T): Promise<*>} opts.commit – persist the value
   * @param {function(Error, T): void} [opts.onError] – called with error and last committed value
   * @param {number} [opts.debounceMs] – debounce interval (default 400)
   * @returns {{ setValue(T): void, flush(Object=): Promise<void>, destroy(): void, dispose(): void }}
   */
  function createDebouncedMutation(opts) {
    const {
      render,
      commit,
      onError,
      debounceMs = DEFAULT_DEBOUNCE_MS,
    } = opts;

    let timer = null;
    let lastCommitted = undefined;
    let inFlight = null;
    let destroyed = false;
    let pendingValue = undefined;
    let replayRequested = false;

    const flushable = {
      key: opts.registryKey || null,
      flush: (options) => api.flush(options),
    };
    const unregisterFlushable = registerFlushable(flushable);

    function doFlush(value, meta = {}) {
      if (destroyed) return Promise.resolve();
      if (typeof value === "undefined") return Promise.resolve();
      if (value === lastCommitted) return Promise.resolve();

      if (inFlight) {
        replayRequested = true;
        return inFlight;
      }

      const target = value;
      inFlight = Promise.resolve(commit(target, meta))
        .then(() => {
          lastCommitted = target;
        })
        .catch((error) => {
          if (!destroyed && typeof onError === "function") {
            onError(error, lastCommitted);
            if (lastCommitted !== undefined) render(lastCommitted);
          }
        })
        .finally(() => {
          inFlight = null;
          if (replayRequested && pendingValue !== lastCommitted) {
            replayRequested = false;
            return doFlush(pendingValue, { reason: "replay" });
          }
          replayRequested = false;
        });

      return inFlight;
    }

    const api = {
      setValue(value) {
        pendingValue = value;
        if (!destroyed) render(value);
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          timer = null;
          doFlush(pendingValue, { reason: "debounce" });
        }, debounceMs);
      },

      async flush(options = {}) {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        await doFlush(pendingValue, {
          reason: options.reason || "flush",
          keepalive: options.keepalive === true,
        });
      },

      setCommitted(value) {
        lastCommitted = value;
      },

      destroy() {
        destroyed = true;
        if (timer) clearTimeout(timer);
        unregisterFlushable();
      },
      dispose() {
        api.destroy();
      },
    };

    return api;
  }

  /**
   * createActionLock – for one-shot actions (cart add, form submit).
   *
   * Provides:
   *  - Single in-flight guard (repeated calls ignored while processing)
   *  - .processing / disabled state management via render callbacks
   *  - Auto-release on completion
   *
   * @param {Object} opts
   * @param {function(): Promise<*>} opts.action – the async action
   * @param {function(boolean): void} opts.render – update UI (true = processing)
   * @param {function(Error): void} [opts.onError] – called on failure
   * @returns {{ execute(...args): Promise<*>, isLocked(): boolean, destroy(): void }}
   */
  function createActionLock(opts) {
    const { action, render, onError } = opts;

    let locked = false;
    let destroyed = false;

    return {
      async execute(...args) {
        if (locked || destroyed) return undefined;
        locked = true;
        render(true);

        try {
          const result = await action(...args);
          return result;
        } catch (error) {
          if (typeof onError === "function") onError(error);
          throw error;
        } finally {
          locked = false;
          if (!destroyed) render(false);
        }
      },

      isLocked() { return locked; },

      destroy() {
        destroyed = true;
      },
    };
  }

  window.MrMapleOptimistic = {
    createOptimisticToggle,
    createDebouncedMutation,
    createActionLock,
    flushAllPending,
    getPersistedToggleIntent,
    setPersistedToggleIntent,
    clearPersistedToggleIntent,
    listPersistedToggleIntents,
  };
})();
